// src/components/ServiceDetailModal.js
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FaSpinner, FaExclamationTriangle, FaTimes } from 'react-icons/fa';
import apiClient from '../api/apiClient'; // Adjust path if needed

// --- Helper Function to Format Eligibility Rules (Keep as is) ---
const formatEligibilityRules = (rules) => {
    if (!rules || typeof rules !== 'object' || !rules.conditions || !Array.isArray(rules.conditions)) {
        return <p style={{ fontStyle: 'italic', color: '#666' }}>Eligibility details not available or in an unexpected format.</p>;
    }
    const { logic = 'AND', conditions } = rules;
    // Basic mappings - Expand as needed
    const attributeMap = { 'user_location_state': 'Your State', 'user_is_bpl': 'Belonging to BPL', 'user_age': 'Your Age' };
    const operatorMap = { '=': 'must be', '>=': 'must be at least', '<=': 'must be at most', '>': 'must be greater than', '<': 'must be less than', '!=': 'must not be', 'includes': 'must include' };
    const formatValue = (value) => (typeof value === 'boolean' ? (value ? 'Yes' : 'No') : value);
    const conditionElements = conditions.map((cond, index) => {
        const attributeName = attributeMap[cond.attribute] || cond.attribute;
        const operatorText = operatorMap[cond.operator] || cond.operator;
        const valueText = formatValue(cond.value);
        return <li key={index}>{attributeName} {operatorText} {valueText}</li>;
    });
    return (
        <>
            <p style={{ marginBottom: '0.5rem', fontStyle: 'italic', color: '#555' }}>{conditions.length > 0 ? `Conditions (${logic === 'AND' ? 'All Required' : 'Any Required'}):` : 'No specific conditions defined.'}</p>
            {conditions.length > 0 && (<ul style={{ paddingLeft: '20px', margin: 0, listStyle: 'disc' }}>{conditionElements}</ul>)}
        </>
    );
};

// --- Helper Function to Format Document Requirements (SIMPLIFIED FOR ARRAY INPUT) ---
const formatDocumentRequirements = (reqs) => {
    // Check if 'reqs' is a valid, non-empty array of strings
    if (!Array.isArray(reqs) || reqs.length === 0) {
        // Handle cases where it's null, undefined, empty array, or not an array
        return <p style={{ fontStyle: 'italic', color: '#666' }}>No specific document requirements listed for this service.</p>;
    }

    // Ensure all items in the array are strings (or filter out non-strings)
    const validReqs = reqs.filter(req => typeof req === 'string' && req.trim());

    if (validReqs.length === 0) {
        return <p style={{ fontStyle: 'italic', color: '#666' }}>No specific document requirements listed for this service.</p>;
    }

    // Render the simple list
    return (
        <ul style={{ paddingLeft: '20px', margin: '0', listStyle: 'disc' }}>
            {validReqs.map((docName, index) => (
                // Use document name and index for a reasonably unique key
                <li key={`${docName.replace(/\s+/g, '-')}-${index}`}>{docName}</li>
            ))}
        </ul>
    );
};


// --- Modal Styles (Keep as is) ---
const modalStyles = {
    modalOverlayStyle: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0, 0, 0, 0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1050, },
    modalContentStyle: { backgroundColor: '#fff', padding: '2rem 2.5rem', borderRadius: '8px', maxWidth: '600px', width: '90%', maxHeight: '85vh', overflowY: 'auto', position: 'relative', boxShadow: '0 5px 15px rgba(0,0,0,0.2)', },
    closeButtonStyle: { position: 'absolute', top: '1rem', right: '1rem', background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#888', lineHeight: 1, padding: '0.25rem', transition: 'color 0.2s ease', },
    closeButtonHoverStyle: { color: '#333' },
    modalTitleStyle: { marginTop: 0, marginBottom: '1.5rem', fontSize: '1.6rem', color: '#2c3e50', borderBottom: '1px solid #eee', paddingBottom: '0.8rem', },
    detailSectionStyle: { marginBottom: '1.2rem', },
    detailLabelStyle: { fontWeight: '600', color: '#555', display: 'block', marginBottom: '0.3rem', fontSize: '0.9rem', },
    detailValueStyle: { color: '#333', whiteSpace: 'pre-wrap', fontSize: '0.95rem', lineHeight: '1.5', margin: 0, },
    loadingIndicatorStyle: { textAlign: 'center', padding: '3rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.8rem', color: '#555', fontSize: '1.1rem', },
    errorIndicatorStyle: { textAlign: 'center', padding: '1.5rem', color: '#c62828', border: '1px solid #ef9a9a', borderRadius: '4px', backgroundColor: '#ffebee', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.6rem', },
    linkStyle: { color: '#4a90e2', textDecoration: 'none', wordBreak: 'break-all', },
    linkHoverStyle: { textDecoration: 'underline' }
};


const ServiceDetailModal = ({ isOpen, onClose, serviceId }) => {
    const [details, setDetails] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (isOpen && serviceId) {
            const fetchDetails = async () => {
                setIsLoading(true); setError(null); setDetails(null);
                try {
                    // Ensure serviceId is treated as a number if needed by the API endpoint, though usually string params work
                    const idParam = serviceId; // Keep as string, typically fine for URL params
                    const response = await apiClient.get(`/services/${idParam}`);
                    console.log(`>>> Data received for Service ID ${idParam}:`, response.data); // Keep for debugging
                    setDetails(response.data);
                } catch (err) {
                     console.error("Error fetching service details:", err);
                     const errorMessage = err.response?.data?.error || err.message || 'Failed to load service details.';
                     setError(errorMessage);
                } finally { setIsLoading(false); }
            };
            fetchDetails();
        } else { setDetails(null); setIsLoading(false); setError(null); }
    }, [isOpen, serviceId]);

    if (!isOpen) return null;

    const handleOverlayClick = (e) => { if (e.target === e.currentTarget) onClose(); };


    return (
        <div style={modalStyles.modalOverlayStyle} onClick={handleOverlayClick}>
            <div style={modalStyles.modalContentStyle} onClick={(e) => e.stopPropagation()}>
                 <button style={modalStyles.closeButtonStyle} onClick={onClose} title="Close" onMouseEnter={(e) => e.currentTarget.style.color = modalStyles.closeButtonHoverStyle.color} onMouseLeave={(e) => e.currentTarget.style.color = modalStyles.closeButtonStyle.color}>
                     <FaTimes />
                 </button>

                 {/* Loading / Error Indicators */}
                 {isLoading && (<div style={modalStyles.loadingIndicatorStyle}><FaSpinner className="fa-spin" size={24} color="#4a90e2" /> Loading details...</div>)}
                 {error && !isLoading && (<div style={modalStyles.errorIndicatorStyle}><FaExclamationTriangle /> {error}</div>)}

                 {/* Details Display */}
                 {details && !isLoading && !error && (
                      <>
                          <h2 style={modalStyles.modalTitleStyle}>{details.name || 'Service Details'}</h2>

                          {/* Standard Detail Sections (Unchanged) */}
                          {details.description && ( <div style={modalStyles.detailSectionStyle}> <span style={modalStyles.detailLabelStyle}>Description:</span> <p style={modalStyles.detailValueStyle}>{details.description}</p> </div> )}
                          <div style={modalStyles.detailSectionStyle}> <span style={modalStyles.detailLabelStyle}>Price:</span> <p style={modalStyles.detailValueStyle}>{details.price > 0 ? `₹${details.price}` : 'Free'}</p> </div>
                          {details.category && ( <div style={modalStyles.detailSectionStyle}> <span style={modalStyles.detailLabelStyle}>Category:</span> <p style={modalStyles.detailValueStyle}>{details.category}</p> </div> )}
                          {details.type && ( <div style={modalStyles.detailSectionStyle}> <span style={modalStyles.detailLabelStyle}>Type:</span> <p style={modalStyles.detailValueStyle}>{details.type}</p> </div> )}
                          {details.state && ( <div style={modalStyles.detailSectionStyle}> <span style={modalStyles.detailLabelStyle}>Availability:</span> <p style={modalStyles.detailValueStyle}>{details.state === 'nationwide' ? 'Nationwide' : details.state}</p> </div> )}

                          {/* Eligibility Rules (Unchanged) */}
                          {details.eligibility_rules && (
                             <div style={modalStyles.detailSectionStyle}>
                                 <span style={modalStyles.detailLabelStyle}>Eligibility Rules:</span>
                                 <div style={modalStyles.detailValueStyle}>
                                     {formatEligibilityRules(details.eligibility_rules)}
                                 </div>
                             </div>
                          )}

                          {/* Document Requirements (Calls the UPDATED helper) */}
                          {/* Removed the check here, let the helper handle null/empty */}
                          <div style={modalStyles.detailSectionStyle}>
                               <span style={modalStyles.detailLabelStyle}>Required Documents:</span>
                               <div style={{...modalStyles.detailValueStyle, whiteSpace: 'normal'}}>
                                    {formatDocumentRequirements(details.document_requirements)}
                               </div>
                          </div>

                          {/* Official Link (Unchanged) */}
                          {details.signup_url && (
                             <div style={modalStyles.detailSectionStyle}>
                                 <span style={modalStyles.detailLabelStyle}>Official Link:</span>
                                 <a href={details.signup_url} target="_blank" rel="noopener noreferrer" style={modalStyles.linkStyle} onMouseEnter={(e) => e.currentTarget.style.textDecoration = modalStyles.linkHoverStyle.textDecoration} onMouseLeave={(e) => e.currentTarget.style.textDecoration = modalStyles.linkStyle.textDecoration}>
                                     {details.signup_url}
                                 </a>
                             </div>
                          )}
                      </>
                  )}
            </div>
        </div>
    );
};

ServiceDetailModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    // serviceId can be number or string depending on how it's passed/used
    serviceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default ServiceDetailModal;